/**
 * Details panel for a single KPI cell: metadata, the manual override, and
 * "Ask about this".
 *
 * SECONDARY by design. The cited document renders in the grid, under the cell
 * itself (`KpiCellExpansion`) — that is where a number gets checked against its
 * source, and this panel deliberately does not repeat it. What is left here is
 * everything that is ABOUT the value rather than evidence for it: when it was
 * extracted, whether the server re-read the cited location, which properties
 * contributed, and the controls that change or discuss it. Reached from the
 * Details button inside the expansion.
 *
 * The override is last and explicit. It is only offered for cells that have a
 * stored row behind them; a calculated or aggregated number has no single row
 * to override, and pretending otherwise would let a user "fix" a total that
 * gets recomputed away on the next run.
 */

import { ExternalLink, MessageSquare, X } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import {
	type KpiCell,
	type KpiMetricRow,
	cellState,
} from "../../types/kpi";
import type {
	KpiOverrideOutcome,
	KpiRevertOutcome,
} from "../../services/kpiService";
import {
	formatExtractedAt,
	formatKpiValueExact,
	formatPeriod,
} from "./formatKpiValue";
import { kpiOverrideTarget } from "./kpiCellAction";
import KpiInputList from "./KpiInputList";
import { useResizablePanel } from "../../hooks/useResizablePanel";
import ResizeHandle from "../ResizeHandle";
import KpiOverrideControl from "./KpiOverrideControl";
import { buildKpiSubstitution } from "./kpiSubstitution";
import KpiSourceLink from "./KpiSourceLink";
import { buildKpiQuestion, kpiChatUrl } from "./kpiChatBridge";

export interface KpiDrillInProps {
	propertyKey: string | null;
	metric: KpiMetricRow;
	period: string;
	/** The table's grain, so the header names the bucket the cell belongs to. */
	grain?: string;
	cell: KpiCell | undefined;
	onClose: () => void;
	onOverride: (valueId: string, value: number) => Promise<KpiOverrideOutcome>;
	/** Undo an override on the edited row, restoring its extracted value. */
	onRevert?: (valueId: string) => Promise<KpiRevertOutcome>;
	/**
	 * A host that takes the question itself (the Analyst rail, DOP-441). Absent,
	 * "Ask about this" hands it to `/chat` as a prefilled draft as before.
	 */
	onAsk?: (question: string) => void;
}

const STATE_CHIP: Record<string, { label: string; className: string }> = {
	verified: {
		label: "Verified",
		className: "bg-emerald-50 text-emerald-700",
	},
	unverified: {
		label: "Unverified",
		className: "bg-amber-50 text-amber-700",
	},
	overridden: {
		label: "Overridden",
		className: "bg-[#E6F2F1] text-[#004C4B]",
	},
	empty: { label: "No value", className: "bg-gray-100 text-text-muted" },
};

/** Narrow enough to stay readable; the panel holds prose and a form. */
export const PANEL_MIN_WIDTH = 320;
export const PANEL_DEFAULT_WIDTH = 420;
/**
 * The panel must never swallow the grid: past ~60% of the viewport the table it
 * explains stops being usable, and the point of the panel is to sit beside a
 * number the user can still see.
 */
export const PANEL_MAX_FRACTION = 0.6;
export const PANEL_WIDTH_STORAGE_KEY = "doppler.kpi.detailsPanelWidth";

/** SSR-safe viewport read; jsdom and the server both need a fallback. */
function viewportWidth(): number {
	return typeof window === "undefined" ? 1280 : window.innerWidth;
}

const KpiDrillIn: React.FC<KpiDrillInProps> = ({
	propertyKey,
	metric,
	period,
	grain = "month",
	cell,
	onClose,
	onOverride,
	onRevert,
	onAsk,
}) => {
	const navigate = useNavigate();
	const maxWidth = Math.max(
		PANEL_MIN_WIDTH,
		Math.round(viewportWidth() * PANEL_MAX_FRACTION),
	);
	const { width, isResizing, startResize, resetWidth, nudgeWidth } =
		useResizablePanel({
			initialWidth: PANEL_DEFAULT_WIDTH,
			minWidth: PANEL_MIN_WIDTH,
			maxWidth,
			edge: "left",
			storageKey: PANEL_WIDTH_STORAGE_KEY,
		});
	// `minWidth` too: the panel is a flex item beside a grid that can be far
	// wider than the viewport, and without it the grid's content would squeeze
	// the panel below the width the user chose.
	const panelStyle = { width, minWidth: width };
	// Draft, saving, conflict and error state all live in KpiOverrideControl now,
	// which is also what a breakdown component uses — one editor, one set of
	// behaviours, wherever a number is corrected.
	const state = cellState(cell);
	const chip = STATE_CHIP[state];
	const source = cell?.source ?? null;
	// v1.3 provenance for computed cells. A pre-v1.3 server sends none, and the
	// panel falls back to the old sentence rather than showing an empty heading.
	const inputs = cell?.inputs ?? [];
	// Which row an override would write to. A computed cell standing on exactly
	// one stored row can be corrected THROUGH it (see `kpiOverrideTarget`); only
	// a number with several rows behind it has no single answer.
	// Same gate the in-grid expansion uses: a computed cell (no stored row of its
	// own) explains itself with the rule it was computed by.
	const formula =
		cell && cell.value_id === null ? metric.formula_display : null;
	// Display-only, and null whenever the equation cannot be reproduced exactly.
	const substitution = buildKpiSubstitution(
		formula,
		inputs,
		cell?.effective_value,
		metric.unit,
	);
	const target = kpiOverrideTarget(cell);
	const overridableId = target.kind === "none" ? null : target.valueId;
	const canOverride = overridableId !== null;
	const passThrough = target.kind === "input" ? target.input : null;
	// The revert button's subject is the STORED row the editor writes to. For a
	// pass-through that is the single input, not the computed cell — the cell's
	// status and value belong to the arithmetic, the input's to the row.
	const isOverridden =
		passThrough !== null
			? passThrough.overridden === true
			: cell?.status === "overridden";
	const extractedValue =
		passThrough !== null
			? passThrough.extracted_value ?? null
			: cell?.value ?? null;

	const question = buildKpiQuestion({
		propertyKey,
		metricLabel: metric.label,
		period,
		grain,
		unit: metric.unit,
		cell,
	});
	const askUrl = kpiChatUrl(question);

	return (
		<aside
			className="relative flex h-full shrink-0 flex-col overflow-y-auto border-l border-gray-200 bg-white"
			style={panelStyle}
		>
			{/* The handle sits on the panel's inner edge, where the grid meets it —
			    the same component and hook the chat's trace panel uses. */}
			<ResizeHandle
				edge="left"
				isResizing={isResizing}
				onPointerDown={startResize}
				onReset={resetWidth}
				onNudge={nudgeWidth}
				value={width}
				min={PANEL_MIN_WIDTH}
				max={maxWidth}
				label="Resize details panel"
			/>
			<header className="flex items-start justify-between gap-2 border-b border-gray-200 px-4 py-3">
				<div className="min-w-0">
					<h2 className="truncate text-base font-medium text-text-base">
						{metric.label}
					</h2>
					<p className="mt-0.5 text-sm text-text-muted">
						{propertyKey ?? "Portfolio"} · {formatPeriod(period, grain)}
					</p>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close details"
					className="rounded p-1 text-text-muted hover:bg-gray-100"
				>
					<X className="h-4 w-4" />
				</button>
			</header>

			<div className="px-4 py-3">
				<div className="flex items-baseline gap-2">
					<span className="text-xl font-semibold tabular-nums text-text-strong">
						{formatKpiValueExact(cell?.effective_value, metric.unit)}
					</span>
					<span
						className={`rounded px-1.5 py-0.5 text-xs font-medium ${chip.className}`}
					>
						{chip.label}
					</span>
				</div>
				{cell?.status === "overridden" && (
					<p className="mt-1 text-sm text-text-muted">
						Extracted value was{" "}
						{formatKpiValueExact(cell.value, metric.unit)}
						{cell.override_by ? ` · overridden by ${cell.override_by}` : ""}
					</p>
				)}
			</div>

			<section className="border-t border-gray-200 px-4 py-3">
				<h3 className="text-sm font-medium text-text-muted">Source</h3>
				{source ? (
					<dl className="mt-2 space-y-1.5">
						<div>
							<dt className="text-xs text-text-muted">File</dt>
							<dd className="break-words text-base text-text-base">
								{/* The whole point of the panel: not "this came from
								    row B18 of a workbook" but the workbook itself,
								    open at B18. */}
								<KpiSourceLink
									source={source}
									cited={cell ? { value: cell.value, unit: cell.unit } : null}
									label={source.file}
								/>
							</dd>
						</div>
						{source.sheet && (
							<div>
								<dt className="text-xs text-text-muted">Sheet</dt>
								<dd className="text-base text-text-base">{source.sheet}</dd>
							</div>
						)}
						{source.ref && (
							<div>
								<dt className="text-xs text-text-muted">Cell</dt>
								<dd className="font-mono text-sm text-text-base">
									{source.ref}
								</dd>
							</div>
						)}
						{source.page && (
							<div>
								<dt className="text-xs text-text-muted">Page</dt>
								<dd className="text-base text-text-base">{source.page}</dd>
							</div>
						)}
						<div>
							<dt className="text-xs text-text-muted">Extracted</dt>
							<dd className="text-base text-text-base">
								{formatExtractedAt(cell?.extracted_at)}
							</dd>
						</div>
					</dl>
				) : inputs.length > 0 ? (
					<>
						{formula && (
							<p className="mt-2 rounded border border-gray-200 bg-gray-50 px-2 py-1.5 font-mono text-xs text-text-base">
								{formula}
								{substitution && (
									// The same equation with today's numbers in it. This is
									// where an override becomes visible as a cause: the
									// component that changed is marked, and the total is the
									// server's own, so the line explains the dashboard rather
									// than competing with it.
									<span
										data-testid="formula-substitution"
										className="mt-1 block text-text-muted"
									>
										={" "}
										{substitution.terms.map((term) => (
											<React.Fragment key={`${term.metricKey}:${term.operator}`}>
												{term.operator && `${term.operator} `}
												<span
													className={
														term.overridden
															? "font-medium text-[#004C4B]"
															: undefined
													}
												>
													{term.text}
													{term.overridden && " (overridden)"}
												</span>{" "}
											</React.Fragment>
										))}
										<span className="font-medium text-text-base">
											= {substitution.total}
										</span>
									</span>
								)}
							</p>
						)}
						<p className="mt-2 text-sm text-text-muted">
							Calculated from these values:
						</p>
						<KpiInputList inputs={inputs} onOverride={onOverride} onRevert={onRevert} />
					</>
				) : (
					<p className="mt-2 text-base text-text-muted">
						{cell?.derived
							? `Calculated from extracted values (${metric.aggregation === "none" ? "derived metric" : metric.aggregation.replace("_", " ")}), so it has no single source cell.`
							: "No source recorded for this value."}
					</p>
				)}
			</section>

			{cell?.contributors && cell.contributors.length > 0 && (
				<section className="border-t border-gray-200 px-4 py-3">
					<h3 className="text-sm font-medium text-text-muted">
						Properties in this total
					</h3>
					{/* Label and value adjacent, not pushed to opposite edges
					    (DOP-294). The panel is resizable up to 60% of the viewport,
					    and at that width `justify-between` reopened the same gap
					    the in-grid tile had: a property name on the left, its
					    number on the right, and nothing pairing them. */}
					<ul className="mt-2 space-y-1">
						{cell.contributors.map((contributor) => (
							<li
								key={contributor.property_key}
								className="flex flex-wrap items-baseline gap-x-2 text-base text-text-base"
							>
								<span className="min-w-0 truncate">
									{contributor.property_key}
								</span>
								<span className="font-medium tabular-nums">
									{formatKpiValueExact(contributor.value, metric.unit)}
								</span>
							</li>
						))}
					</ul>
				</section>
			)}

			<section className="border-t border-gray-200 px-4 py-3">
				<h3 className="text-sm font-medium text-text-muted">Manual override</h3>
				{canOverride && overridableId !== null ? (
					<KpiOverrideControl
						valueId={overridableId}
						unit={metric.unit}
						extractedValue={extractedValue}
						isOverridden={isOverridden}
						onRevert={onRevert}
						label={
							passThrough ? (
								// Name the row being corrected. The user clicked a portfolio
								// figure; the number that changes is one property's, and a
								// field that does not say so invites an edit to the wrong
								// thing.
								<>
									Correcting{" "}
									<span className="font-medium text-text-base">
										{passThrough.property_key ?? "the stored value"}
									</span>{" "}
									· {passThrough.metric_key}
									{passThrough.value !== null && passThrough.value !== undefined
										? ` (currently ${formatKpiValueExact(
												passThrough.value,
												passThrough.unit ?? metric.unit,
											)})`
										: ""}
								</>
							) : undefined
						}
						onOverride={onOverride}
					/>
				) : (
					<p className="mt-2 text-base text-text-muted">
						This number is calculated from several values, so there is no single
						one to correct. Open the breakdown and override the value you mean.
					</p>
				)}
			</section>

			<section className="mt-auto border-t border-gray-200 px-4 py-3">
				<button
					type="button"
					onClick={() => (onAsk ? onAsk(question) : navigate(askUrl))}
					className="flex w-full items-center justify-center gap-2 rounded border border-gray-300 px-3 py-2 text-sm font-medium text-text-base hover:bg-gray-50"
				>
					<MessageSquare className="h-4 w-4" />
					Ask about this
					{/* The arrow says "this leaves the page"; with a host listening
					    the question stays beside the grid, so it goes. */}
					{!onAsk && <ExternalLink className="h-3 w-3 text-text-muted" />}
				</button>
			</section>
		</aside>
	);
};

export default KpiDrillIn;
